import _ from 'lodash';
import moment from 'moment';
import MoviesService from './movies.service';
import ImdbMovie from './imdb-movie.schema';
import { decodeHtmlEntities } from './html-utils';


// inspired by imdb scraper found here:
// https://github.com/mhsjlw/imdb


export default class ImdbHtmlLinkedDataParser {

  static get ValidContentRatings() {
    return MoviesService.MPAARatingOptions;
  }

  imdbMovieId = null;
  data = null;

  constructor(rawData, imdbMovieId = null) {
    this.imdbMovieId = imdbMovieId;
    this.data = rawData || null;
  }

  parseAll() {
    let movie = null;
    if (this.data) {
      movie = new ImdbMovie();
      movie.imdb_id = this.imdbMovieId;

      movie.title = this.getTitle();
      movie.release_date = this.getReleaseDate();
      movie.release_year = this.getYear();

      let posterUrls = this.getPosterUrls();
      movie.imdb_img = _.first(posterUrls) || null;
      movie.imdb_imgs = posterUrls;

      movie.rating = this.getContentRating();
      movie.duration_mins = this.getRuntimeMinutes();
      movie.synopsis = this.getSynopsis();
      movie.star_rating = this.getStarRating();
      movie.metascore = this.getMetaScore();

      movie.all_genres = this.getGenres();
      movie.all_writers = this.getWriters();
      movie.all_directors = this.getDirectors();
      movie.all_cast = this.getCast();

      movie.genre = _.join(movie.all_genres, ', ');
      movie.cast = _.join(movie.all_cast, ', ');
    }
    return movie;
  }

  getTitle() {
    let str = _.get(this.data, 'name');
    if (str) {
      str = _.trim(_.replace(str, /\(\d+\)/g, ''));
      str = decodeHtmlEntities(str);
    }
    return str;
  }

  getSynopsis() {
    let str = _.get(this.data, 'description');
    if (str) {
      str = _.trim(str);
      str = decodeHtmlEntities(str);
    }
    return !_.isEmpty(str)? str: null;
  }

  getYear() {
    let dateStr = _.get(this.data, 'datePublished');
    if (dateStr) {
      let m = _.toString(dateStr).match(/^(\d{4})/);
      if (m) {
        return parseInt(m[1]);
      }
    }
    return null;
  }

  getReleaseDate() {
    let str = _.get(this.data, 'datePublished');
    if (str && _.isString(str)) {
      let tmpDate = null;
      try {
        tmpDate = moment(new Date(str));
      } catch (ex) {}
      if (tmpDate && tmpDate.isValid()) {
        return tmpDate.format('YYYY-MM-DD');
      }
    }
    return null;
  }

  getPosterUrls() {
    let poster1 = this.data.image;
    let poster1ResizedA = _.replace(poster1, /\._[^.]+_(\.\w+)$/, '$1');    // hacked to grab resized media at 1000px tall.  Updated 12/14/2020 to account for new URL syntax

    return _.chain([ poster1ResizedA, poster1 ])
      .map(_.trim)
      .uniq()
      .compact()
      .value();
  }

  getRuntimeMinutes() {
    let mins = null;
    let str = this.data.duration;
    if (str) {
      let d = moment.duration(str);
      if (d && d.isValid()) {
        mins = d.asMinutes();
      }
    }
    return mins;
  }

  getCast() {
    let actors = this.data.actor;
    actors = this._ensureArray(actors);
    actors = _.chain(actors)
      .map(v => {
        return (v && _.toLower(v['@type']) === 'person' && v.name)? v.name: null;
      })
      .map(_.trim)
      .map(v => decodeHtmlEntities(v))
      .compact()
      .uniq()
      .slice(0, 3)
      .value();
    return !_.isEmpty(actors)? actors: null;
  }

  getWriters() {
    let creators = this.data.creator;
    creators = this._ensureArray(creators);
    creators = _.chain(creators)
      .map(v => {
        return (v && _.toLower(v['@type']) === 'person' && v.name)? v.name: null;
      })
      .map(_.trim)
      .map(v => decodeHtmlEntities(v))
      .compact()
      .uniq()
      .slice(0, 3)
      .value();
    return !_.isEmpty(creators)? creators: null;
  }

  getDirectors() {
    let directors = this.data.director;
    directors = this._ensureArray(directors);
    directors = _.chain(directors)
      .map(v => {
        return (v && _.toLower(v['@type']) === 'person' && v.name)? v.name: null;
      })
      .map(_.trim)
      .map(v => decodeHtmlEntities(v))
      .compact()
      .uniq()
      .slice(0, 3)
      .value();
    return !_.isEmpty(directors)? directors: null;
  }

  getContentRating() {
    let str = _.trim(this.data.contentRating);
    let rating = !_.isEmpty(str)? str: null;
    let validRatings = this.constructor.ValidContentRatings;
    if (!_.isEmpty(validRatings)) {
      let validRating = _.find(validRatings, v => {
        return (_.toLower(v.label) === _.toLower(rating));
      });
      rating = _.get(validRating, 'value') || null;
    }

    return rating;
  }

  getStarRating() {
    let val = _.get(this.data, 'aggregateRating.ratingValue');
    return this._parseFloat(val);
  }

  getMetaScore() {
    return null;
  }

  getGenres() {
    let strs = this.data.genre;
    let genreItems = _.chain(strs)
      .map(_.trim)
      .uniq()
      .compact()
      .slice(0, 3)    // only grab the first 3 items
      .value();
    return !_.isEmpty(genreItems)? genreItems: null;
  }

  _parseInt(str, defaultValue = null) {
    let retval = defaultValue;
    if (str) {
      let tmp = parseInt(str);
      if (_.isFinite(tmp)) {
        retval = tmp;
      }
    }
    return retval;
  }

  _parseFloat(str, defaultValue = null) {
    let retval = defaultValue;
    if (str) {
      let tmp = parseFloat(str);
      if (_.isFinite(tmp)) {
        retval = tmp;
      }
    }
    return retval;
  }

  _ensureArray(val) {
    if (_.isNull(val) || _.isUndefined(val)) {
      val = [];
    } else if (!_.isArray(val)) {
      val = [val];
    }
    return val;
  }

}


